import React, { FC, useEffect, useMemo, useState } from 'react'
import { ChevronDown } from 'react-bootstrap-icons'
import IconRightArrow from '@/components/IconRightArrow/IconRightArrow'
import { shouldShowViewAll } from '@/domain/browseBy/useCases/shouldShowViewAll'
import { shouldShowShowMore } from '@/domain/browseBy/useCases/shouldShowShowMore'
import {
  CLIENTELE_LINKS,
  CONDITIONS_LINKS,
  DRUGS_LINKS,
  INSURANCE_LINKS,
  ITEMS_VISIBLE_THRESHOLD_DESKTOP,
  ITEMS_VISIBLE_THRESHOLD_MOBILE,
  POPULAR_DESTINATIONS_LINKS,
  STATES_LINKS
} from '@/domain/browseBy/constants'
import { SearchState, Term } from '@/types/models'
import { isNonEmptyObject } from '@/utils/checks'
import { LinkItem } from './BrowseByLinkItem'

export type Link = {
  title: string
  subtitle?: string
  url: string
}

export type TabData = {
  links: Link[]
  viewAll?: Link
}

const defaultTabs: string[] = [
  'Popular Destinations',
  'States',
  'Conditions',
  'Drugs',
  'Insurance',
  'Clientele'
]
const items: Record<string, TabData> = {
  'Popular Destinations': {
    links: POPULAR_DESTINATIONS_LINKS,
    viewAll: { title: 'All Locations', url: '/locations/' }
  },
  States: {
    links: STATES_LINKS,
    viewAll: { title: 'Show All', url: '/united-states-locations/' }
  },
  Conditions: {
    links: CONDITIONS_LINKS,
    viewAll: { title: 'All Conditions', url: '/condition/' }
  },
  Drugs: {
    links: DRUGS_LINKS
  },
  Insurance: {
    links: INSURANCE_LINKS,
    viewAll: { title: 'Show All', url: '/insurance/' }
  },
  Clientele: {
    links: CLIENTELE_LINKS
  }
}

const isDefaultLinkItems = (linkItems: Record<string, TabData>) =>
  isNonEmptyObject(linkItems) &&
  Object.keys(linkItems).includes('Popular Destinations')

const BrowseBy: FC<{
  linkItems: Record<string, TabData>
  title: string
  searchState?: SearchState
  term?: Term
}> = ({ linkItems = items, title = 'Browse by', searchState, term }) => {
  const [activeTab, setActiveTab] = useState<string>(
    isDefaultLinkItems(linkItems)
      ? 'Popular Destinations'
      : Object.keys(linkItems)[0]
  )
  const [showMore, setShowMore] = useState<boolean>(false)
  const [isMobile, setIsMobile] = useState(true)
  const tabs = Object.keys(linkItems) || defaultTabs

  useEffect(() => {
    setIsMobile(window.matchMedia('(max-width: 767px)').matches)
  }, [])

  const handleTabClick = (tab: string) => {
    setActiveTab(tab)
    setShowMore(false)
  }

  const currentThreshold = useMemo(
    () =>
      isMobile
        ? ITEMS_VISIBLE_THRESHOLD_MOBILE
        : ITEMS_VISIBLE_THRESHOLD_DESKTOP,
    [isMobile]
  )

  const shouldShowViewAllButton = useMemo(
    () => shouldShowViewAll(linkItems, activeTab, showMore, currentThreshold),
    [activeTab, showMore, currentThreshold]
  )

  const showShowShowMoreButton = useMemo(
    () => shouldShowShowMore(linkItems, activeTab, showMore, currentThreshold),
    [linkItems, activeTab, showMore, currentThreshold]
  )

  const shouldShowTabs =
    isNonEmptyObject(linkItems) && Object.keys(linkItems).length > 1

  return (
    <div className="flex w-full flex-col items-start gap-6 py-8 text-gray-1100 md:gap-8 md:py-20 xl:min-h-[444px]">
      <div className="flex w-full items-center justify-between">
        <h2 className="max-w-[300px] font-FieldsVariable text-2xl font-medium text-gray-1100 sm:max-w-[400px] xl:max-w-none">
          {title}
        </h2>
      </div>
      {shouldShowTabs && (
        <div className="-mx-4 w-screen md:-mx-6 lg:-mx-8 xl:mx-0 xl:w-full">
          <div className="hide-scroll flex w-full overflow-x-scroll px-4 md:px-6 lg:px-8 xl:px-0">
            {tabs.map((tab) => (
              <div
                key={tab}
                className="border-b border-stone-300 px-4 first:pl-0 last:pr-0"
              >
                <button
                  className={`whitespace-nowrap px-px pb-[11px] pt-px text-sm font-medium ${
                    activeTab === tab
                      ? 'border-b-2 border-stone-600 text-gray-1100'
                      : 'text-gray-700'
                  }`}
                  onClick={() => handleTabClick(tab)}
                >
                  {tab}
                </button>
              </div>
            ))}
            <div className="grow border-b border-stone-300"></div>
          </div>
        </div>
      )}
      <div className="grid w-full grid-cols-2 gap-x-4 gap-y-3 md:grid-cols-6">
        {linkItems[activeTab]?.links?.map((item, index) => (
          <LinkItem
            key={`${item.title}-${index}`}
            item={item}
            show={showMore || index < currentThreshold}
            searchState={searchState}
            term={term}
          />
        ))}
        {shouldShowViewAllButton && (
          <div>
            <a
              href={linkItems[activeTab]?.viewAll?.url}
              className="flex items-center gap-1 text-sm font-semibold xl:no-underline xl:hover:underline"
            >
              <span className="flex-0 min-w-0">
                {linkItems[activeTab]?.viewAll?.title}
              </span>
              <IconRightArrow className="shrink-0" />
            </a>
          </div>
        )}
        {showShowShowMoreButton && (
          <ShowMoreButton
            key={`${activeTab}-show-more`}
            showMore={showMore}
            setShowMore={setShowMore}
            itemCount={linkItems[activeTab]?.links?.length}
            currentThreshold={currentThreshold}
          />
        )}
      </div>
    </div>
  )
}

type ShowMoreButtonProps = {
  showMore: boolean
  setShowMore: React.Dispatch<React.SetStateAction<boolean>>
  itemCount: number
  currentThreshold: number
}

const ShowMoreButton: FC<ShowMoreButtonProps> = ({
  showMore,
  setShowMore,
  itemCount,
  currentThreshold
}) => {
  if (showMore || itemCount <= currentThreshold) {
    return null
  }

  return (
    <div>
      <button
        className="flex items-center justify-center gap-2 text-sm font-semibold xl:no-underline xl:hover:underline"
        onClick={() => setShowMore(true)}
      >
        <span>Show More</span>
        <ChevronDown className="size-4" />
      </button>
    </div>
  )
}

export default BrowseBy
